import { useState, useEffect } from "react";
import {
  Box,
  Autocomplete,
  Chip,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { PoButton } from "./common/PoButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { LIST } from "../config/const";
// config
import { TableColumn, DataColumn } from "../config/dataInterface";
// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";
// PoForm 部品（cmd-po_1148: SerchSideBar PoForm 化）
import {
  PoField,
  PoLabel,
  PoTextField,
  PoSelect,
  PoHelper,
  poAutocompleteSx,
} from "./common/PoForm";

// ─── catalog .sp-input sx（isSmartPhone && !embeddedInModal で height 44 切替）───
// 呼び出し側から isSmartPhone/embeddedInModal を渡して生成する
// ★rw1: specificity を &&& に上げて poAutocompleteSx（&&）に勝つよう是正
function buildSpInputSx(isSmartPhone: boolean, embeddedInModal: boolean) {
  const spH = isSmartPhone && !embeddedInModal ? "var(--control-h-sp)" : "32px";
  return {
    // テキスト/日付入力（非 Autocomplete）
    "&&& .MuiOutlinedInput-root": {
      height: spH,
      padding: "0 var(--sp-2)",
      border: "1px solid var(--color-border)",
      borderRadius: "var(--radius-sm)",
      fontSize: "var(--fs-12)",
      color: "var(--color-sumi)",
      backgroundColor: "var(--color-white)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    // Autocomplete inputRoot（.sp-chips 相当・height auto で伸縮）
    "&&& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
      height: "auto",
      minHeight: spH,
      padding: "var(--sp-1) var(--sp-2)",
      flexWrap: "wrap",
      gap: "var(--sp-1)",
      alignItems: "center",
    },
    "& .MuiOutlinedInput-notchedOutline": { border: 0 },
  } as const;
}

// catalog .sp-helper sx
const spHelperSx = {
  fontSize: "var(--fs-10)",
  color: "var(--color-sumi-muted)",
  marginTop: "1px",
} as const;

// catalog .po-chip sx
const poChipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sp-1)",
  height: "22px",
  padding: "0 var(--sp-2)",
  borderRadius: "11px",
  fontSize: "var(--fs-11)",
  fontWeight: "var(--fw-semibold)",
  backgroundColor: "var(--color-washi-warm)",
  color: "var(--color-sumi)",
} as const;

// Autocomplete inputRoot sp-chips 相当 sx（.MuiAutocomplete-inputRoot に適用）
// ★rw1: &&& に上げて poAutocompleteSx（&&）に順序非依存で勝つ
const spChipsSx = {
  "&&& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
    flexWrap: "wrap",
    gap: "var(--sp-1)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-sm)",
    padding: "var(--sp-1) var(--sp-2)",
    minHeight: "58px",
    alignItems: "center",
    height: "auto",
  },
  "&&&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot": { paddingRight: "calc(var(--sp-6) + var(--sp-5) + var(--sp-1))" },
  "&& .MuiOutlinedInput-notchedOutline": { border: 0 },
  "&& .MuiAutocomplete-input": { padding: 0, height: "auto", minWidth: "60px" },
  "&& .MuiAutocomplete-endAdornment": { right: "var(--sp-2)", top: "50%", transform: "translateY(-50%)" },
  "&& .MuiInputBase-input::placeholder": { color: "var(--color-sumi-muted)", opacity: 1 },
} as const;

function SerchInputField({
  inputList,
  label: _label,       // 外側 PoLabel で管理するため内部では不使用
  marginLeft: _ml,     // PoField の幅で管理するため不使用
  width: _w,           // 同上
  value,
  setValue,
  marginTop: _mt,      // 同上
  disabled,
  onKeyDown,
  popperContainerRef,
  popperContainer,
  embeddedInModal,
}: {
  inputList: string[];
  label: string;
  marginLeft: string;
  width: string;
  value: string;
  setValue: (value: string) => void;
  marginTop: string;
  disabled: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  popperContainer?: HTMLElement | null;
  embeddedInModal?: boolean;
}) {
  const container = popperContainer ?? popperContainerRef?.current;
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const spInputSx = buildSpInputSx(isSmartPhone, embeddedInModal ?? false);
  return (
    <Autocomplete
      disabled={disabled}
      clearIcon={null}
      options={inputList}
      value={value}
      sx={{ ...poAutocompleteSx, ...spInputSx }}
      slotProps={{
        popper: {
          ...(container ? { container } : {}),
          ...(embeddedInModal ? { sx: { zIndex: 1500 } } : {}),
        },
      }}
      onChange={(_event: any, newValue: string | null) => {
        setValue(newValue ?? "");
      }}
      renderInput={(params) => (
        <PoTextField
          {...params}
          onKeyDown={onKeyDown}
          sx={spInputSx}
        />
      )}
    />
  );
}

// 複数選択対応フィールドのキー一覧
const MULTI_SELECT_KEYS = new Set([
  // Contract
  "category_name",
  "high_rank_company_name",
  "product_name",
  "sale_company_name",
  "sale_company_group_name",
  "toss_up_company_name",
  "toss_up_account_group_name",
  "toss_up_account_name",
  "follow_call_status",
  "sale_account_name",
  // TossUp
  "toss_up_status",
  "category_name_list",
  "product_name_list",
  "calling_account_name",
  "toss_up_company_group_name",
  "closer_company_name",
  "closer_account_name",
  "closer_product_name",
  // Service (明細管理) / FollowCall / Incentive / OutsideEntryForm
  "entry_sale_company_name",
  "payment_sale_company_name",
  // PaymentConfirm (確定済明細一覧)
  "staff_account_name",
  // ScreenColumnConfig (システム項目設定)
  "login_sale_company",
  // SaleCompany (パートナー管理)
  "group_name",
  "toss_up_product_name",
  "entry_product_name",
  "collaborate_company_name",
  // EntryAnalysys 表示区分（複数選択）
  "entry_analysys_division_admin",
  "entry_analysys_division_partner",
  // TossUpAnalysys / ClosingAnalysys 表示区分（複数選択）
  "toss_up_analysys_division_admin",
  "toss_up_analysys_division_partner",
]);

function SerchMultiInputField({
  inputList,
  value,
  setValue,
  disabled,
  popperContainerRef,
  popperContainer,
  embeddedInModal,
  onKeyDown,
}: {
  inputList: string[];
  value: string;
  setValue: (value: string) => void;
  disabled: boolean;
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  popperContainer?: HTMLElement | null;
  embeddedInModal?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const container = popperContainer ?? popperContainerRef?.current;
  const selectedValues = value ? value.split(",").filter((v) => v !== "") : [];
  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={inputList}
      value={selectedValues}
      sx={spChipsSx}
      slotProps={{
        popper: {
          ...(container ? { container } : {}),
          ...(embeddedInModal ? { sx: { zIndex: 1500 } } : {}),
        },
      }}
      onChange={(_event: any, newValues: string[]) => {
        setValue(newValues.join(","));
      }}
      renderInput={(params) => (
        <TextField {...params} sx={{ "& .MuiInputBase-root": { fontSize: "var(--fs-12)" } }} onKeyDown={onKeyDown} />
      )}
      renderTags={(tagValues: string[], getTagProps) =>
        tagValues.map((option: string, index: number) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option}
              {...tagProps}
              size="small"
              className={`${tagProps.className ?? ""} po-chip`.trim()}
              sx={poChipSx}
            />
          );
        })
      }
    />
  );
}

function SerchTextField({
  label,
  helperText,
  value,
  setValue,
  onKeyDown,
  testId,
}: {
  label: string;
  helperText: string;
  value: string;
  setValue: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  testId?: string;
}) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const embeddedInModal = false; // SerchTextField は常に非embedded（embeddedInModal 分岐なし）
  const spInputSx = buildSpInputSx(isSmartPhone, embeddedInModal);
  return (
    <PoField testId={testId} fieldKey={testId ?? label}>
      <PoLabel testId={testId ? testId + "-label" : undefined}>{label}</PoLabel>
      <PoTextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        sx={spInputSx}
      />
      <PoHelper sx={{ ...spHelperSx, textAlign: "right" }}>{helperText}</PoHelper>
    </PoField>
  );
}

function SerchDateField({
  label,
  valueStart,
  setValueStart,
  valueEnd,
  setValueEnd,
  onKeyDown,
  testId,
  embeddedInModal,
}: {
  label: string;
  valueStart: string;
  setValueStart: (value: string) => void;
  valueEnd: string;
  setValueEnd: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  testId?: string;
  embeddedInModal?: boolean;
}) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const spInputSx = buildSpInputSx(isSmartPhone, embeddedInModal ?? false);
  return (
    <PoField testId={testId} fieldKey={testId ?? label}>
      <PoLabel testId={testId ? testId + "-label" : undefined}>{label}</PoLabel>
      <Box
        data-testid={testId ? testId + "-range" : undefined}
        sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-1)" }}
      >
        <PoTextField
          type="date"
          value={valueStart}
          onChange={(e) => setValueStart(e.target.value)}
          onKeyDown={onKeyDown}
          sx={spInputSx}
        />
        <Box
          data-testid="search-range-tilde"
          sx={{ fontSize: "var(--fs-11)", color: "var(--color-sumi-muted)", pl: "var(--sp-05)" }}
        >〜</Box>
        <PoTextField
          type="date"
          value={valueEnd}
          onChange={(e) => setValueEnd(e.target.value)}
          onKeyDown={onKeyDown}
          sx={spInputSx}
        />
      </Box>
    </PoField>
  );
}

interface SerchSideBarProps {
  setSerchOpen: (open: boolean) => void;
  tableColumn: TableColumn;
  dataColumn: DataColumn[];
  firstYear: number;
  thisYear: number;
  serchColumn: { [key: string]: string };
  setSerchString: (value: { [key: string]: string }) => void;
  companyNameList: string[];
  companyGroupNameList: string[];
  highRankCompanyNameList: string[];
  categoryNameList: string[];
  productNameList: string[];
  productCandidateMap?: { [key: string]: string[] };
  accountSaleNameList: string[];
  accountStaffNameList: string[];
  serchStringView: { [key: string]: string };
  setSerchStringView: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  /** モーダル内でドロップダウンを表示する際に指定（例: 詳細モーダル） */
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  /** モーダル内で Popper を描画するコンテナ要素（ref は初回 null になり得るため、親で state 保持して渡す） */
  popperContainer?: HTMLElement | null;
  /** トスアップ分析 / クロージング分析で表示するリストを切り替える */
  analysisSubType?: "tossup" | "closing";
  /** モーダル内に埋め込む場合。高さを親に合わせ、検索ボタンを常に表示する */
  embeddedInModal?: boolean;
  /** ランキングページの表示範囲設定（日別/月別） */
  displayMode?: "日別 / 月別" | "日別" | "月別";
  setDisplayMode?: (mode: "日別 / 月別" | "日別" | "月別") => void;
  displayModeList?: string[];
}

function SerchSideBar(props: SerchSideBarProps) {
  const {
    setSerchOpen,
    tableColumn,
    dataColumn,
    firstYear,
    thisYear,
    serchColumn,
    setSerchString,
    companyNameList,
    companyGroupNameList,
    highRankCompanyNameList,
    categoryNameList,
    productNameList,
    productCandidateMap,
    accountSaleNameList,
    accountStaffNameList,
    serchStringView,
    setSerchStringView,
    popperContainerRef,
    popperContainer,
    analysisSubType,
    embeddedInModal = false,
    displayMode,
    setDisplayMode,
    displayModeList,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);

  const getTossUpListKey = (key: string): string => {
    if (analysisSubType === "tossup") {
      if (key === "toss_up_analysys_division_admin")
        return "toss_up_analysys_division_admin_tossup";
      if (key === "toss_up_analysys_division_partner")
        return "toss_up_analysys_division_partner_toss_up";
      if (key === "toss_up_analysys_count_type")
        return "toss_up_analysys_count_type_toss_up";
      if (key === "toss_up_aggregation_item")
        return "toss_up_aggregation_item_tossup";
      if (key === "toss_up_analysys_sub_count")
        return "toss_up_analysys_sub_count_tossup";
    }
    if (analysisSubType === "closing") {
      if (key === "toss_up_analysys_division_admin")
        return "toss_up_analysys_division_admin_closing";
      if (key === "toss_up_analysys_division_partner")
        return "toss_up_analysys_division_partner_closing";
      if (key === "toss_up_analysys_count_type")
        return "toss_up_analysys_count_type_closing";
      if (key === "toss_up_aggregation_item")
        return "toss_up_aggregation_item_closing";
      if (key === "toss_up_analysys_sub_count")
        return "toss_up_analysys_sub_count_closing";
    }
    return key;
  };

  /** 集計項目表示 → entry_analysys_count_type */
  const entryAggregationToCountType = (agg: string): string => {
    if (agg === "エントリー数") return "システム登録日";
    if (agg === "開通数") return "開通日";
    if (agg === "解約数") return "解約日";
    return "システム登録日";
  };
  /** entry_analysys_count_type → 集計項目表示 */
  const entryCountTypeToAggregation = (ct: string): string => {
    if (ct === "システム登録日") return "エントリー数";
    if (ct === "開通日") return "開通数";
    if (ct === "解約日") return "解約数";
    return "エントリー数";
  };
  const pathname = useAtomValue(AppAtoms.pathname);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const createMonthList = () => {
    let monthList = [];
    for (let i = 1; i <= 12; i++) {
      monthList.push(i.toString());
    }
    return monthList;
  };
  const createYearList = () => {
    let yearList = [];
    for (let i = firstYear; i <= thisYear; i++) {
      yearList.push(i.toString());
    }
    return yearList;
  };

  const [screenDataColumn, setScreenDataColumn] = useState<DataColumn[]>([]);
  useEffect(() => {
    setScreenDataColumn(
      dataColumn.length > 0 ? dataColumn : tableColumn.dataColumn,
    );
  }, [tableColumn, dataColumn]);

  // エンターキーで検索を実行するハンドラー
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).getAttribute('aria-expanded') === 'true') return;
    if (e.key === "Enter") {
      e.preventDefault();
      setSerchString(serchStringView);
      if (isSmartPhone) {
        setSerchOpen(false);
      }
    }
  };
  return (
    <Box
      data-testid="search-panel"
      sx={{
        /* SP形: catalog .search-sheet 全面固定オーバーレイ — 既知事故(×ボタンヘッダ被り)再発防止 */
        position: isSmartPhone && !embeddedInModal ? "fixed" : undefined,
        inset: isSmartPhone && !embeddedInModal ? 0 : undefined,
        zIndex: isSmartPhone && !embeddedInModal ? 1150 : undefined,
        /* PC形・embedded形: 幅300px右ドック */
        minWidth: isSmartPhone && !embeddedInModal ? undefined : "300px",
        maxWidth: isSmartPhone && !embeddedInModal ? undefined : "300px",
        /* ★rw1: maxHeight:100% を除去 → alignSelf:stretch+内部minHeight:0 で高さチェーンを閉じる */
        alignSelf: isSmartPhone && !embeddedInModal ? undefined : "stretch",
        height: embeddedInModal ? "100%" : undefined,
        border: (isSmartPhone || embeddedInModal) ? undefined : "1px solid var(--color-border)",
        borderLeft: isSmartPhone && !embeddedInModal ? undefined : "3px solid var(--color-kohaku)",
        display: "flex",
        flexDirection: "column",
        overflow: isSmartPhone && !embeddedInModal ? undefined : "hidden",
        background: "var(--color-white)",
        ...(isSmartPhone
          ? {}
          : embeddedInModal
            ? {
                borderTopLeftRadius: "var(--radius-md)",
                borderBottomLeftRadius: "var(--radius-md)",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }
            : { borderRadius: "var(--radius-md)" }),
        boxShadow: (isSmartPhone || embeddedInModal) ? undefined : "var(--shadow-card)",
      }}
    >
      <Box
        sx={{
          margin: embeddedInModal ? "0 10px" : isSmartPhone ? 0 : "15px 10px",
          flex: "1 1 0",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isSmartPhone && !embeddedInModal ? (
          <Box
            data-testid="search-sheet-head"
            sx={{
              height: "var(--header-h)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px",
              borderBottom: "1px solid var(--color-border)",
              flexShrink: 0,
            }}
          >
            <Box sx={{ fontSize: "var(--fs-14, 14px)", fontWeight: 700, color: "var(--color-ai)" }}>検索</Box>
            <IconButton
              data-testid="search-panel-close-btn"
              onClick={() => setSerchOpen(false)}
              size="small"
              sx={{ width: "28px", height: "28px", color: "var(--color-sumi-light)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box
            data-testid="search-sp-head"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--sp-3) var(--sp-4)",
              flexShrink: 0,
              borderBottom: "1px solid var(--color-border-light)",
            }}
            key="serchTitleBox"
          >
            <Box
              sx={{ fontSize: "var(--fs-14, 14px)", fontWeight: 700, color: "var(--color-ai)" }}
            >
              検索
            </Box>
            <IconButton onClick={() => setSerchOpen(false)} size="small" sx={{ width: "28px", height: "28px", color: "var(--color-ai)" }}>
              <CloseIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Box>
        )}
        <Box
          data-testid="search-sp-body"
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            padding: isSmartPhone ? "var(--sp-3) var(--sp-4)" : "var(--sp-1)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
          }}
        >
          {displayMode !== undefined && setDisplayMode && (
            <PoField testId="search-display-mode-wrap" fieldKey="display-mode">
              <PoLabel testId="search-display-mode-label">表示範囲設定</PoLabel>
              <PoSelect
                data-testid="search-field-display-mode"
                value={displayMode}
                onChange={(e) =>
                  setDisplayMode(
                    e.target.value as "日別 / 月別" | "日別" | "月別",
                  )
                }
                sx={{ fontSize: "14px", width: "100%" }}
              >
                {(displayModeList ?? LIST["display_mode"]).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </PoSelect>
            </PoField>
          )}
          {screenDataColumn.map((dataColumn, i) => {
            if (dataColumn.serch === true) {
              if (
                loginUser &&
                dataColumn.tableDetailAuth.includes(loginUser.authority)
              ) {
                if (dataColumn.serchType === "bubun") {
                  return (
                    <SerchTextField
                      key={"serchColumnBubun" + i}
                      label={dataColumn.name}
                      helperText="※部分一致"
                      value={serchStringView[dataColumn.key]}
                      setValue={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                      testId="search-field-bubun"
                    />
                  );
                } else if (dataColumn.serchType === "kanzen") {
                  return (
                    <SerchTextField
                      key={"serchColumnKanzen" + i}
                      label={dataColumn.name}
                      helperText="※完全一致"
                      value={serchStringView[dataColumn.key]}
                      setValue={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                      testId="search-field-kanzen"
                    />
                  );
                } else if (dataColumn.serchType === "date") {
                  return (
                    <SerchDateField
                      key={"serchColumnDate" + i}
                      label={dataColumn.name}
                      valueStart={serchStringView[dataColumn.key + "_start"]}
                      setValueStart={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key + "_start"]: value,
                        }));
                      }}
                      valueEnd={serchStringView[dataColumn.key + "_end"]}
                      setValueEnd={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key + "_end"]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                      testId="search-field-date"
                      embeddedInModal={embeddedInModal}
                    />
                  );
                } else if (dataColumn.serchType === "analysys_year_month") {
                  if (serchStringView.analysys_granularity == "日別") {
                    return (
                      <PoField key={"serchColumnAnalysysYearMonth" + i} testId="search-field-analysys_year_month" fieldKey="analysys_year_month">
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchInputField
                          key={"serchEndYearColumn" + i}
                          inputList={createYearList()}
                          label="年"
                          marginLeft="5px"
                          width="100px"
                          value={serchStringView.analysys_granularity_date_year}
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_date_year: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <SerchInputField
                          key={"serchEndMonthColumn" + i}
                          inputList={createMonthList()}
                          label="月"
                          marginLeft="10px"
                          width="75px"
                          value={
                            serchStringView.analysys_granularity_date_month
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_date_month: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </PoField>
                    );
                  } else if (serchStringView.analysys_granularity == "月別") {
                    return (
                      <PoField key={"serchColumnAnalysysMonthly" + i} testId="search-field-analysys_year_month" fieldKey="analysys_year_month">
                        <PoLabel>{dataColumn.name}</PoLabel>
                        {/* 月別フル幅5要素縦積み（殿合意ASCII図・cmd-po_1180） */}
                        <SerchInputField
                          key={"serchStartYearColumn" + i}
                          inputList={createYearList()}
                          label="年"
                          marginLeft="0"
                          width="100%"
                          value={
                            serchStringView.analysys_granularity_month_start_year
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_start_year: value,
                            }));
                          }}
                          marginTop="0"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <SerchInputField
                          key={"serchStartMonthColumn" + i}
                          inputList={createMonthList()}
                          label="月"
                          marginLeft="0"
                          width="100%"
                          value={
                            serchStringView.analysys_granularity_month_start_month
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_start_month: value,
                            }));
                          }}
                          marginTop="0"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        {/* 〜 区切り行（独立行） */}
                        <Box sx={{ py: "var(--sp-1)", color: "var(--color-sumi-light)" }}>〜</Box>
                        <SerchInputField
                          key={"serchEndYearColumn" + i}
                          inputList={createYearList()}
                          label="年"
                          marginLeft="0"
                          width="100%"
                          value={
                            serchStringView.analysys_granularity_month_end_year
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_end_year: value,
                            }));
                          }}
                          marginTop="0"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <SerchInputField
                          key={"serchEndMonthColumn" + i}
                          inputList={createMonthList()}
                          label="月"
                          marginLeft="0"
                          width="100%"
                          value={
                            serchStringView.analysys_granularity_month_end_month
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_end_month: value,
                            }));
                          }}
                          marginTop="0"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </PoField>
                    );
                  }
                } else if (dataColumn.serchType === "runking_day") {
                  return (
                    <PoField key={"serchColumnRunkingDay" + i} testId="search-field-runking_day" fieldKey="runking_day">
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <PoTextField
                        type="date"
                        value={serchStringView.runking_granularity_date}
                        onChange={(e) =>
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            runking_granularity_date: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                        sx={{ width: "100%" }}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "payment_year_month") {
                  return (
                    <PoField key={"serchColumnPaymentYearMonth" + i} testId="search-field-payment_year_month" fieldKey="payment_year_month">
                      <PoLabel testId="search-field-payment_year_month-label">{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchEndYearColumn" + i}
                        inputList={createYearList()}
                        label="年"
                        marginLeft="5px"
                        width="100px"
                        value={serchStringView.payment_year}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            payment_year: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                      <SerchInputField
                        key={"serchEndMonthColumn" + i}
                        inputList={createMonthList()}
                        label="月"
                        marginLeft="10px"
                        width="75px"
                        value={serchStringView.payment_month}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            payment_month: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "list") {
                  const isEntryAggregation =
                    dataColumn.key === "entry_aggregation_item";
                  const listLabel = dataColumn.name;
                  const inputList = isEntryAggregation
                    ? LIST["entry_aggregation_item"]
                    : analysisSubType
                      ? dataColumn.key ===
                          "toss_up_analysys_division_partner" &&
                        loginUser?.db_authority === "トスアップアカウント"
                        ? LIST[
                            "toss_up_analysys_division_partner_toss_up_account"
                          ]
                        : LIST[getTossUpListKey(dataColumn.key)]
                      : dataColumn.key === "toss_up_analysys_count_type" &&
                          loginUser?.db_authority === "トスアップアカウント"
                        ? LIST["toss_up_analysys_count_type_toss_up"]
                        : LIST[dataColumn.key];
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnListMulti" + i} testId="search-field-list" fieldKey={dataColumn.key} data-multi="true">
                        <PoLabel>{listLabel}</PoLabel>
                        <SerchMultiInputField
                          inputList={inputList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  const displayValue = isEntryAggregation
                    ? entryCountTypeToAggregation(
                        serchStringView.entry_analysys_count_type ?? "",
                      )
                    : serchStringView[dataColumn.key];
                  return (
                    <PoField key={"serchColumnList" + i} testId="search-field-list" fieldKey={dataColumn.key}>
                      <PoLabel>{listLabel}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={inputList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={displayValue}
                        setValue={(value) => {
                          if (isEntryAggregation) {
                            setSerchStringView((prev) => ({
                              ...prev,
                              entry_aggregation_item: value,
                              entry_analysys_count_type:
                                entryAggregationToCountType(value),
                            }));
                          } else if (
                            dataColumn.key === "entry_analysys_count_type" &&
                            value === "売上"
                          ) {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                              analysys_granularity: "月別",
                            }));
                          } else {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                            }));
                          }
                        }}
                        marginTop="10px"
                        disabled={
                          analysisSubType
                            ? false
                            : dataColumn.key ===
                                  "toss_up_analysys_division_partner" &&
                                loginUser?.db_authority ===
                                  "トスアップアカウント"
                              ? true
                              : dataColumn.key ===
                                    "toss_up_analysys_count_type" &&
                                  loginUser?.db_authority ===
                                    "トスアップアカウント"
                                ? true
                                : dataColumn.key === "analysys_granularity" &&
                                    serchStringView.entry_analysys_count_type ===
                                      "売上"
                                  ? true
                                  : false
                        }
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "company") {
                  if (
                    loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ||
                    ((loginUser?.authority === "パートナー" ||
                      loginUser?.authority === "営業アカウント") &&
                      (pathname == "/toss_up" || pathname == "/contract"))
                  ) {
                    if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                      return (
                        <PoField key={"serchColumnCompanyMulti" + i} testId="search-field-company" fieldKey={dataColumn.key}>
                          <PoLabel>{dataColumn.name}</PoLabel>
                          <SerchMultiInputField
                            inputList={companyNameList}
                            value={serchStringView[dataColumn.key] || ""}
                            setValue={(value) => {
                              setSerchStringView((prev) => ({
                                ...prev,
                                [dataColumn.key]: value,
                              }));
                            }}
                            disabled={false}
                            popperContainerRef={popperContainerRef}
                            popperContainer={popperContainer}
                            embeddedInModal={embeddedInModal}
                            onKeyDown={handleKeyDown}
                          />
                        </PoField>
                      );
                    }
                    return (
                      <PoField key={"serchColumnCompany" + i} testId="search-field-company" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchInputField
                          key={"serchColumnList" + i}
                          inputList={companyNameList}
                          label=""
                          marginLeft="5px"
                          width="100%"
                          value={serchStringView[dataColumn.key]}
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </PoField>
                    );
                  }
                } else if (dataColumn.serchType === "group") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnGroupMulti" + i} testId="search-field-group" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={companyGroupNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnGroup" + i} testId="search-field-group" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={companyGroupNameList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "high_rank_company") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnHighRankMulti" + i} testId="search-field-high_rank_company" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={highRankCompanyNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnHighRank" + i} testId="search-field-high_rank_company" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={highRankCompanyNameList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "category") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnCategoryMulti" + i} testId="search-field-category" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={categoryNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnCategory" + i} testId="search-field-category" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={categoryNameList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "product") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnProductMulti" + i} testId="search-field-product" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={
                            (productCandidateMap &&
                              productCandidateMap[dataColumn.key]) ??
                            productNameList
                          }
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnProduct" + i} testId="search-field-product" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={
                          (productCandidateMap &&
                            productCandidateMap[dataColumn.key]) ??
                          productNameList
                        }
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "staff_account") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnStaffMulti" + i} testId="search-field-staff_account" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={accountStaffNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnStaff" + i} testId="search-field-staff_account" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={accountStaffNameList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        onKeyDown={handleKeyDown}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "product_partner_month") {
                  const isDaily = displayMode === "日別";
                  return (
                    <PoField key={"serchColumnProductPartnerMonth" + i} testId="search-field-product_partner_month" fieldKey="product_partner_month">
                      <PoLabel>{dataColumn.name}</PoLabel>
                      {!isDaily ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-1)" }}>
                          <PoTextField
                            type="month"
                            value={serchStringView["start_month"] || ""}
                            onChange={(e) =>
                              setSerchStringView((prev) => ({
                                ...prev,
                                start_month: e.target.value,
                              }))
                            }
                            onKeyDown={handleKeyDown}
                            sx={{ width: "100%" }}
                          />
                          <Box sx={{ fontSize: "11px", color: "var(--color-sumi-muted)", paddingLeft: "2px" }}>〜</Box>
                          <PoTextField
                            type="month"
                            value={serchStringView["end_month"] || ""}
                            onChange={(e) =>
                              setSerchStringView((prev) => ({
                                ...prev,
                                end_month: e.target.value,
                              }))
                            }
                            onKeyDown={handleKeyDown}
                            sx={{ width: "100%" }}
                          />
                        </Box>
                      ) : (
                        <PoTextField
                          type="month"
                          value={serchStringView["year_month"] || ""}
                          onChange={(e) =>
                            setSerchStringView((prev) => ({
                              ...prev,
                              year_month: e.target.value,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                          sx={{ width: "100%" }}
                        />
                      )}
                    </PoField>
                  );
                } else if (dataColumn.serchType === "contract_year_month") {
                  // 契約支払予定月: 単月検索 (cmd-po_1192) — serchType=contract_year_month
                  // serchStringView[dataColumn.key] を使用（各列ごとに独立したキー）
                  return (
                    <PoField key={"serchColumnContractYearMonth" + i} testId="search-field-contract_year_month" fieldKey={dataColumn.key}>
                      <PoLabel testId="search-field-contract_year_month-label">{dataColumn.name}</PoLabel>
                      <PoTextField
                        type="month"
                        value={serchStringView[dataColumn.key] || ""}
                        onChange={(e) =>
                          setSerchStringView((prev) => ({
                            ...prev,
                            [dataColumn.key]: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                        sx={{ ...buildSpInputSx(isSmartPhone, embeddedInModal ?? false), width: "100%" }}
                      />
                    </PoField>
                  );
                } else if (dataColumn.serchType === "sale_account") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <PoField key={"serchColumnSaleAccountMulti" + i} testId="search-field-sale_account" fieldKey={dataColumn.key}>
                        <PoLabel>{dataColumn.name}</PoLabel>
                        <SerchMultiInputField
                          inputList={accountSaleNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                          onKeyDown={handleKeyDown}
                        />
                      </PoField>
                    );
                  }
                  return (
                    <PoField key={"serchColumnSaleAccount" + i} testId="search-field-sale_account" fieldKey={dataColumn.key}>
                      <PoLabel>{dataColumn.name}</PoLabel>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={accountSaleNameList}
                        label=""
                        marginLeft="5px"
                        width="100%"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </PoField>
                  );
                }
              }
            }
          })}
        </Box>
        {isSmartPhone && !embeddedInModal ? (
          <Box sx={{ height: "60px" }} />
        ) : null}
        <Box
          data-testid="search-sp-foot"
          sx={{
            display: "flex",
            gap: "var(--sp-2)",
            flexShrink: 0,
            padding: "var(--sp-3) var(--sp-4)",
            borderTop: "1px solid var(--color-border-light)",
            position: isSmartPhone && !embeddedInModal ? "fixed" : undefined,
            bottom: isSmartPhone && !embeddedInModal ? 0 : undefined,
            left: isSmartPhone && !embeddedInModal ? 0 : undefined,
            backgroundColor: isSmartPhone && !embeddedInModal ? "var(--color-white)" : undefined,
            width: isSmartPhone && !embeddedInModal ? "100%" : undefined,
          }}
        >
          {!embeddedInModal && (
            <PoButton
              variant="secondary"
              sx={{ flex: 1, height: "var(--control-h, 36px)" }}
              onClick={async () => {
                setSerchString(serchColumn);
                setSerchStringView(serchColumn);
                if (isSmartPhone) {
                  setSerchOpen(false);
                }
              }}
            >
              検索クリア
            </PoButton>
          )}
          <PoButton
            variant="primary"
            sx={{ flex: 1, height: "var(--control-h, 36px)" }}
            onClick={async () => {
              setSerchString(serchStringView);
              if (isSmartPhone) {
                setSerchOpen(false);
              }
            }}
          >
            <Box>検索</Box>
            <SearchIcon
              sx={{ marginLeft: "6px", color: "white" }}
              fontSize="medium"
            />
          </PoButton>
        </Box>
      </Box>
    </Box>
  );
}

export default SerchSideBar;
export { SerchSideBar };
